import React from 'react';
import {
  Document,
  BLOCKS,
  INLINES,
  Block,
  Inline
} from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';

import {
  ICtaDestination,
  IRegistrationEntrypointFields
} from '@/types/contentful';
import type { AffiliateHero2Props, greenlightBrandingEnum } from '../index';

import RichText from '~/components/RichText';

import CtaWidget from '../../../CtaWidget';
import { AffiliateLogoGrouping } from '../AffiliateLogoGrouping';
import PrivacyPolicyDisclaimer from '../../../PrivacyPolicyDisclaimer';
import { Asset } from '../../../AtomicComponents/Asset';
import cx from 'classnames';

interface PropsWithOverride extends AffiliateHero2Props {
  appsFlyerOverride?: string;
}

export default function MobileView({
  mobileHeadingText,
  mobileSubheadingText,
  mobileSubheadingTextRich,
  mobileImage,
  mobileCta,
  affiliateLogo,
  useGreenlightIcon,
  greenlightBranding,
  privacyPolicyDisclaimer,
  displayPrivacyPolicyIcon,
  sendConsentEvent,
  useEmbeddedSmartLink,
  hideLogoOnMobile,
  appsFlyerOverride,
  leftAlignOnMobile
}: PropsWithOverride) {
  const SUBHEADING_RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return <>{children}</>;
    },
    [INLINES.HYPERLINK]: (node, children) => {
      const href = node.data.uri as string;
      return (
        <a
          href={href}
          className="underline transition-colors duration-200 ease-in-out hover:opacity-80"
        >
          {children}
        </a>
      );
    }
  };

  const renderSubheading = (text: string | Document | undefined) => {
    if (!text) return null;

    if (typeof text === 'string') {
      return <span>{text}</span>;
    }

    if (text && typeof text === 'object' && 'nodeType' in text) {
      return (
        <RichText
          data={text as Document}
          overrideOptions={SUBHEADING_RICH_TEXT_OVERRIDE}
        />
      );
    }

    return null;
  };

  return (
    <div className="block tablet:hidden">
      {!hideLogoOnMobile && (
        <AffiliateLogoGrouping
          affiliateLogo={affiliateLogo}
          useGreenlightIcon={useGreenlightIcon}
          greenlightBranding={greenlightBranding as greenlightBrandingEnum}
        />
      )}
      <div className="column w-full">
        <div className="relative flex-1">
          {mobileImage && (
            <div
              className="tablet:hidden"
              data-testid="affiliate-hero-20-mobile-background"
            >
              <Asset asset={mobileImage} loading="eager" />
            </div>
          )}
        </div>
      </div>

      <div className="block-container mt-8" data-e2e="hero">
        <div className="layout layout-padding">
          <div className="column w-11/12 tablet:w-8/12">
            <div
              className={cx(
                'mx-auto flex flex-col',
                leftAlignOnMobile
                  ? 'items-start justify-start'
                  : 'items-center justify-center'
              )}
              data-testid="affiliate-hero-20-hero-content-mobile"
            >
              <h1
                className={cx(
                  'mx-auto pb-2 text-[34px] leading-[2.25rem]',
                  leftAlignOnMobile ? 'text-left' : 'text-center'
                )}
                data-testid="affiliate-hero-20-hero-headline-mobile"
              >
                {(mobileHeadingText || '').replace(/<br>/g, ' ')}
              </h1>
              <div
                className={cx(
                  'mx-auto mt-4 pb-4 text-xl leading-[23px]',
                  leftAlignOnMobile ? 'text-left' : 'text-center'
                )}
                data-testid="affiliate-hero-20-hero-subheading-mobile"
              >
                {renderSubheading(
                  mobileSubheadingTextRich ?? mobileSubheadingText
                )}
              </div>

              {mobileCta && (
                <CtaWidget
                  {...mobileCta?.fields}
                  id="affiliate-hero-mobile-cta-widget"
                  sysId={mobileCta?.sys?.contentType?.sys?.id}
                  module="Hero"
                  sendConsentEvent={sendConsentEvent}
                  url={
                    useEmbeddedSmartLink && appsFlyerOverride
                      ? ({
                          fields: {
                            production: appsFlyerOverride,
                            staging: appsFlyerOverride,
                            development: appsFlyerOverride
                          }
                        } as ICtaDestination)
                      : (mobileCta?.fields as IRegistrationEntrypointFields)
                          ?.url
                  }
                />
              )}

              {privacyPolicyDisclaimer && (
                <div className="mt-6">
                  <PrivacyPolicyDisclaimer
                    privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                    displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
